// ============================================================================
// Bouquet Intent Category System
// ============================================================================
// Purpose: Deterministic intent classification for AI content generation.
// Each bouquet belongs to ONE of 6 categories, and AI generates content
// strictly within the constraints of the selected category.
//
// Design: Fowler - Make Implicit Explicit
// - No recipient/relationship logic (only visual factors)
// - Category is FIXED at creation time and stored in DB
// ============================================================================
/**
 * Primary intent categories for bouquet messaging.
 * These define the emotional purpose the bouquet communicates.
 *
 * @example
 * - Red roses + intense composition -> CONFESS
 * - Soft pastels + peonies -> RECONCILE
 * - Bright mixed colors -> THANK
 */
export var BouquetIntentCategory;
(function (BouquetIntentCategory) {
    /** Помириться - Apologize, seek forgiveness, mend relationships */
    BouquetIntentCategory["RECONCILE"] = "RECONCILE";
    /** Признаться - Declare romantic feelings, express love */
    BouquetIntentCategory["CONFESS"] = "CONFESS";
    /** Поддержать - Show support, comfort, be there for someone */
    BouquetIntentCategory["SUPPORT"] = "SUPPORT";
    /** Поблагодарить - Express gratitude, appreciation */
    BouquetIntentCategory["THANK"] = "THANK";
    /** Отметить событие - Celebrate occasions (birthday, anniversary, etc.) */
    BouquetIntentCategory["CELEBRATE"] = "CELEBRATE";
    /** Показать внимание - Show attention, "thinking of you" */
    BouquetIntentCategory["ATTENTION"] = "ATTENTION";
})(BouquetIntentCategory || (BouquetIntentCategory = {}));
/**
 * Human-readable labels for each category (multilingual).
 * Used in Admin UI and customer-facing displays.
 */
export const INTENT_CATEGORY_LABELS = {
    [BouquetIntentCategory.RECONCILE]: {
        az: 'Barışmaq',
        ru: 'Помириться',
        en: 'Reconcile',
    },
    [BouquetIntentCategory.CONFESS]: {
        az: 'Etiraf etmək',
        ru: 'Признаться',
        en: 'Confess Love',
    },
    [BouquetIntentCategory.SUPPORT]: {
        az: 'Dəstək olmaq',
        ru: 'Поддержать',
        en: 'Support',
    },
    [BouquetIntentCategory.THANK]: {
        az: 'Təşəkkür etmək',
        ru: 'Поблагодарить',
        en: 'Thank',
    },
    [BouquetIntentCategory.CELEBRATE]: {
        az: 'Bayram etmək',
        ru: 'Отметить событие',
        en: 'Celebrate',
    },
    [BouquetIntentCategory.ATTENTION]: {
        az: 'Diqqət göstərmək',
        ru: 'Показать внимание',
        en: 'Show Attention',
    },
};
/**
 * Short descriptions for each category (for tooltips/help text).
 */
export const INTENT_CATEGORY_DESCRIPTIONS = {
    [BouquetIntentCategory.RECONCILE]: {
        az: 'Üzr istəmək, bağışlanma diləmək',
        ru: 'Извиниться, попросить прощения',
        en: 'Apologize, seek forgiveness',
    },
    [BouquetIntentCategory.CONFESS]: {
        az: 'Romantik hissləri ifadə etmək',
        ru: 'Признаться в романтических чувствах',
        en: 'Express romantic feelings',
    },
    [BouquetIntentCategory.SUPPORT]: {
        az: 'Dəstək və rahatlıq vermək',
        ru: 'Выразить поддержку и утешение',
        en: 'Show support and comfort',
    },
    [BouquetIntentCategory.THANK]: {
        az: 'Minnətdarlıq bildirmək',
        ru: 'Выразить благодарность',
        en: 'Express gratitude',
    },
    [BouquetIntentCategory.CELEBRATE]: {
        az: 'Ad günü, yubiley və digər hadisələr',
        ru: 'День рождения, годовщина и другие события',
        en: 'Birthday, anniversary and other occasions',
    },
    [BouquetIntentCategory.ATTENTION]: {
        az: 'Sadəcə düşündüyünü göstərmək',
        ru: 'Просто показать, что думаешь о ком-то',
        en: 'Simply show you are thinking of someone',
    },
};
/**
 * All valid occasion codes (for validation).
 */
export const VALID_OCCASION_CODES = [
    'birthday',
    'anniversary',
    'wedding',
    'valentines',
    'mothers_day',
    'graduation',
    'congratulations',
    'novruz',
];
/**
 * Occasion code labels (multilingual).
 */
export const OCCASION_CODE_LABELS = {
    birthday: { az: 'Ad günü', ru: 'День рождения', en: 'Birthday' },
    anniversary: { az: 'İldönümü', ru: 'Годовщина', en: 'Anniversary' },
    wedding: { az: 'Toy', ru: 'Свадьба', en: 'Wedding' },
    valentines: { az: 'Sevgililər günü', ru: 'День влюблённых', en: "Valentine's Day" },
    mothers_day: { az: 'Analar günü', ru: 'День матери', en: "Mother's Day" },
    graduation: { az: 'Məzuniyyət', ru: 'Выпускной', en: 'Graduation' },
    congratulations: { az: 'Təbrik', ru: 'Поздравление', en: 'Congratulations' },
    novruz: { az: 'Novruz', ru: 'Новруз', en: 'Novruz' },
};
